import { cn } from '@heroui/react';

import ButtonV2 from '../Button';
import Typography from '../Typography';

interface AppUpdateBannerProps {
  /** Human-readable app name shown in the banner message. */
  appName?: string;
  /** Called when the user clicks the reload button. */
  onReload: () => void;
  /** Shows a spinner on the button and disables it while the reload is in progress. */
  isLoading?: boolean;
  className?: string;
}

/**
 * Fixed top-of-viewport banner that notifies the user a new build is
 * available and prompts them to reload. Not dismissible — the caller
 * controls visibility by mounting/unmounting this component.
 *
 * Detection logic is intentionally kept out of this component so it can
 * be driven by a service-worker update hook in the consuming app.
 */
const AppUpdateBanner = ({
  appName = 'the app',
  onReload,
  isLoading = false,
  className
}: AppUpdateBannerProps) => (
  <div
    role="status"
    aria-live="polite"
    className={cn(
      'fixed top-0 inset-x-0 z-[9999] bg-primary text-white shadow-md',
      className
    )}
  >
    <div className="flex items-center justify-center gap-3 px-4 py-2">
      <Typography
        variant="body-2"
        weight="medium"
        className="!text-white text-center"
      >
        A new version of {appName} is available.
      </Typography>
      <ButtonV2
        color="tertiary"
        size="sm"
        onPress={onReload}
        isLoading={isLoading}
        isDisabled={isLoading}
      >
        Refresh app
      </ButtonV2>
    </div>
  </div>
);

export default AppUpdateBanner;
